import { Button, Collapsible, CollapsibleContent, CollapsibleTrigger, cn } from '@repo/ui'
import { ArrowDown, ChevronDown, LoaderCircle } from 'lucide-react'
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { ConversationCommand, ConversationMessage, ConversationQuestion } from '@/api/conversation'
import { ConversationTurn, turnArtifact } from './Turn'
import {
  ConversationChangeCard,
  ConversationExhaustedCard,
  ConversationIntakeStoppedCard,
  ConversationStoppedCard
} from './Projections'
import { ConversationExecutionDisclosure } from './Progress'
import { ConversationQuestionControls } from './QuestionControls'
import { stageLabel } from './eventCopy'
import { ConversationNavigator } from './ConversationNavigator'
import { conversationNavigationItems, useConversationNavigationPosition } from './navigation'
import { isTranscriptActivity, selectConversationTurnPresentations, selectSlideVersionFacts } from './state'
import type { ReactNode, Ref } from 'react'
import type { ChatInspectorIdentity, WorkspaceCanvasIdentity } from './ConversationShell'
import type { ConversationClarification, ConversationOriginStatus, ConversationState, TranscriptEntry } from './state'
import type { SourceSummaryLoader } from './SourceDetails'

const followThreshold = 120

type HistoryReadAnchor = { element: HTMLElement; offset: number }
type PendingHistoryLoad = {
  historyKey: string
  anchor: HistoryReadAnchor | undefined
  mode: 'reading' | 'jump' | 'end'
  settled: boolean
  frame?: number
}
type HistoryLoadState = {
  threadId: ConversationState['threadId']
  loading: boolean
  error: string | undefined
}

function historyReadAnchor(scroller: HTMLElement): HistoryReadAnchor | undefined {
  const top = scroller.getBoundingClientRect().top
  const readingLine = top + Math.min(96, scroller.clientHeight / 4)
  let anchor: HistoryReadAnchor | undefined
  for (const element of scroller.querySelectorAll<HTMLElement>('[data-conversation-turn-key]')) {
    const offset = element.getBoundingClientRect().top - top
    if (anchor !== undefined && offset + top > readingLine) break
    anchor = { element, offset }
  }
  return anchor
}

function isNearConversationEnd(element: HTMLElement): boolean {
  return element.scrollHeight - element.scrollTop - element.clientHeight <= followThreshold
}

export function conversationScrollBehavior(): ScrollBehavior {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
}

function scrollToConversationEnd(element: HTMLElement, behavior: ScrollBehavior): void {
  if (typeof element.scrollTo === 'function') {
    element.scrollTo({ top: element.scrollHeight, behavior })
    return
  }
  element.scrollTop = element.scrollHeight
}

export function conversationProjectionRevision(state: ConversationState): string {
  const messages = state.liveMessages
    .map((message) => `${message.messageId}:${message.updatedAtTsMs}:${message.content}`)
    .join('|')
  const attempts = Object.values(state.attempts)
    .map(
      (attempt) =>
        `${attempt.executionId}:${attempt.stepKey}:${attempt.attemptEpoch}:${attempt.lastSequence}:${attempt.text}`
    )
    .join('|')
  const questions = state.questions
    .map((question) => `${question.runId}:${question.suspensionKey}:${question.briefVersion}:${question.slotKey}`)
    .join('|')
  // The progress indicator appears and disappears as work starts and finishes, and its
  // trail collapses when a stage leaves the announced graph, so execution state belongs in
  // the revision. Without it the scroller does not follow the indicator arriving under the
  // last turn. The trail does not grow step by step: every row is rendered on the first
  // frame carrying the graph, and a step boundary only re-labels them.
  const stages = Object.entries(state.executionStates)
    .map(([executionId, execution]) => `${executionId}:${execution.status ?? ''}:${execution.stage ?? ''}`)
    .join('|')
  const intake = state.tasks.map((task) => `${task.intakeRunId}:${task.intakeStatus}:${task.intakeStage}`).join('|')
  return `${messages}\n${attempts}\n${questions}\n${stages}\n${intake}\n${state.changes.at(-1)?.eventId ?? 0}`
}

function SlideResultDisclosure({ count, children }: { count: number; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const summary = `Updated ${count} slides`
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="flex flex-col gap-2">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex min-h-8 w-fit max-w-full items-center gap-2 rounded-md px-2 text-left text-[0.8125rem] text-muted-foreground outline-none hover:bg-accent/50 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={summary}
        >
          <ChevronDown
            className={cn('size-3.5 shrink-0 transition-transform motion-reduce:transition-none', open && 'rotate-180')}
            aria-hidden="true"
          />
          <span>{summary}</span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-3">{children}</CollapsibleContent>
    </Collapsible>
  )
}

function ClarificationDisclosure({ clarifications }: { clarifications: Array<ConversationClarification> }) {
  const [open, setOpen] = useState(false)
  const detailCount = clarifications.reduce((count, clarification) => count + clarification.slots.length, 0)
  const summary = `Clarified ${detailCount} ${detailCount === 1 ? 'detail' : 'details'}`
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="flex flex-col gap-1">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex min-h-8 w-fit max-w-full items-center gap-2 rounded-md px-2 text-left text-[0.8125rem] text-muted-foreground outline-none hover:bg-accent/50 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={summary}
        >
          <ChevronDown
            className={cn('size-3.5 shrink-0 transition-transform motion-reduce:transition-none', open && 'rotate-180')}
            aria-hidden="true"
          />
          <span>{summary}</span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <dl className="flex flex-col gap-3 border-l border-border/70 py-1 pl-3.5">
          {clarifications.map((clarification) => (
            <div key={clarification.message.messageId} className="grid gap-0.5">
              <dt className="text-xs font-medium text-muted-foreground">
                {clarification.slots.map((slot) => slot.label).join(', ')}
              </dt>
              <dd className="text-sm leading-6 break-words whitespace-pre-wrap">{clarification.message.content}</dd>
            </div>
          ))}
        </dl>
      </CollapsibleContent>
    </Collapsible>
  )
}

function resultArtifactKey(message: ConversationMessage): string | undefined {
  const artifact = turnArtifact(message)
  if (artifact?.weight !== 'card') return undefined
  const identity = artifact.canvas
  switch (identity.canvas) {
    case 'proposal':
      return `proposal:${identity.proposalId}:${identity.sectionId ?? ''}`
    case 'presentation':
      return `presentation:${identity.presentationId}:${identity.slideId ?? ''}`
    case 'document':
      return `document:${identity.documentId}`
    case 'file':
    case 'text':
    case 'connector':
    case 'collection':
      return undefined
  }
}

function PendingQuestion({
  question,
  latest,
  articleRef,
  onAnswer,
  onAbandon
}: {
  question: ConversationQuestion
  latest: boolean
  articleRef?: Ref<HTMLElement>
  onAnswer: (command: Extract<ConversationCommand, { type: 'answer' }>) => Promise<void>
  onAbandon?: (briefId: number, runId: number) => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const controls = (
    <ConversationQuestionControls
      question={question}
      onAnswer={onAnswer}
      {...(onAbandon === undefined ? {} : { onAbandon })}
    />
  )
  if (latest) {
    return (
      <article
        ref={articleRef}
        tabIndex={-1}
        data-testid="conversation-question-turn"
        data-conversation-question-turn
        className="scroll-mb-24 py-1 outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="Snowvault question"
      >
        {controls}
      </article>
    )
  }
  return (
    <article data-testid="conversation-question-turn" data-conversation-question-turn aria-label="Snowvault question">
      <Collapsible open={open} onOpenChange={setOpen} className="flex flex-col gap-2">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex min-h-10 w-fit max-w-full items-center gap-2 rounded-md px-2 text-left text-sm text-muted-foreground outline-none hover:bg-accent/50 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Needs your answer: ${question.prompt}`}
          >
            <ChevronDown
              className={cn(
                'size-3.5 shrink-0 transition-transform motion-reduce:transition-none',
                open && 'rotate-180'
              )}
              aria-hidden="true"
            />
            <span>Needs your answer</span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="py-1">{controls}</CollapsibleContent>
      </Collapsible>
    </article>
  )
}

export function ConversationTranscript({
  state,
  projectName,
  currentUserId,
  onLoadOlder,
  onOpenCanvas,
  onOpenInspector,
  onAnswer,
  onAbandonQuestion,
  onContinueQuestions,
  onRetryExecution,
  loadSourceSummaries,
  olderError
}: {
  state: ConversationState
  projectName?: string
  currentUserId: number
  onLoadOlder: () => Promise<void>
  onOpenCanvas: (identity: WorkspaceCanvasIdentity) => void
  onOpenInspector: (identity: ChatInspectorIdentity) => void
  onAnswer?: (command: Extract<ConversationCommand, { type: 'answer' }>) => Promise<void>
  onAbandonQuestion?: (briefId: number, runId: number) => Promise<void>
  onContinueQuestions?: (briefId: number, runId: number) => Promise<void>
  onRetryExecution?: (executionId: number) => Promise<void>
  loadSourceSummaries?: SourceSummaryLoader
  olderError?: string
}) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const navigationPrefix = useId()
  const navigationPausedRef = useRef(false)
  const pendingHistoryRef = useRef<PendingHistoryLoad>(undefined)
  const historyKeyRef = useRef('')
  const questionRef = useRef<HTMLElement>(null)
  const revealedQuestionRef = useRef<string>(undefined)
  const followingRef = useRef(true)
  const followingEndRef = useRef(false)
  const [newActivity, setNewActivity] = useState(false)
  const [historyLoad, setHistoryLoad] = useState<HistoryLoadState>({
    threadId: state.threadId,
    loading: false,
    error: undefined
  })
  if (historyLoad.threadId !== state.threadId) {
    setHistoryLoad({ threadId: state.threadId, loading: false, error: undefined })
  }
  const loadingOlder = historyLoad.threadId === state.threadId && historyLoad.loading
  const loadOlderError = historyLoad.threadId === state.threadId ? historyLoad.error : undefined
  const messages = useMemo(
    () =>
      [...state.olderMessages, ...state.liveMessages].sort(
        (a, b) => a.createdAtTsMs - b.createdAtTsMs || a.messageId - b.messageId
      ),
    [state.liveMessages, state.olderMessages]
  )
  const outputByMessageID = useMemo(
    () => new Map(state.outputs.map((output) => [output.messageId, output])),
    [state.outputs]
  )
  // Depend on the slices the selector reads rather than on state itself: the reducer
  // returns a fresh state object for every frame, and most of them touch nothing the
  // transcript orders by.
  const turns = useMemo(
    () => selectConversationTurnPresentations(state),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- these eleven slices are what the selector reads
    [
      state.olderMessages,
      state.liveMessages,
      state.olderTasks,
      state.tasks,
      state.changes,
      state.attempts,
      state.questions,
      state.executionStates,
      state.executionStartedAt,
      state.exhausted,
      state.pendingCanonicalMessageIds
    ]
  )
  // A slide turn and the change event for the version it wrote are one write reported twice.
  // The turn takes the change's verb and word delta, and the change's own line is dropped:
  // without this the slide's name appeared twice, once with the verb and once with the key
  // message, and neither line said the whole thing.
  const slideFacts = useMemo(
    () => selectSlideVersionFacts(state),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- these three slices are what the selector reads
    [state.changes, state.liveMessages, state.olderMessages]
  )
  const projectionRevision = conversationProjectionRevision(state)
  const navigationItems = useMemo(() => conversationNavigationItems(turns), [turns])
  const navigationPosition = useConversationNavigationPosition(
    scrollerRef,
    contentRef,
    navigationItems.map((item) => item.key).join('\n'),
    state.threadId
  )
  const isEmpty =
    !state.hasOlder &&
    messages.length === 0 &&
    Object.keys(state.attempts).length === 0 &&
    state.questions.length === 0 &&
    state.changes.length === 0 &&
    // A running execution is activity even before its turn has paged in, and the hero
    // asking what you need must not sit above a spinning progress bar.
    Object.keys(state.executionStates).length === 0

  const answerableQuestion = onAnswer === undefined ? undefined : state.questions.at(-1)
  const answerableQuestionKey =
    answerableQuestion === undefined
      ? undefined
      : `${answerableQuestion.runId}:${answerableQuestion.suspensionKey}:${answerableQuestion.slotKey}`
  const announcedTurn = turns.at(-1)
  const liveAnnouncement =
    announcedTurn?.phase === 'waiting'
      ? `Question ready: ${announcedTurn.questions.at(-1)?.prompt.slice(0, 160) ?? 'Your answer is needed'}`
      : announcedTurn?.phase === 'working'
        ? announcedTurn.activeSummary || stageLabel(announcedTurn.activeStage) || 'Working'
        : announcedTurn?.phase === 'writing'
          ? announcedTurn.activeSummary || stageLabel(announcedTurn.activeStage) || 'Writing the response'
          : announcedTurn?.phase === 'submitted'
            ? announcedTurn.activeSummary || stageLabel(announcedTurn.activeStage) || 'Preparing your request'
            : announcedTurn?.phase === 'stopped'
              ? 'Work stopped'
              : announcedTurn?.tasks.length
                ? 'Complete'
                : ''

  useLayoutEffect(() => {
    historyKeyRef.current = state.olderMessages.map((message) => message.messageId).join('|')
  }, [state.olderMessages])

  useLayoutEffect(() => {
    if (pendingHistoryRef.current?.frame !== undefined) cancelAnimationFrame(pendingHistoryRef.current.frame)
    pendingHistoryRef.current = undefined
    const scroller = scrollerRef.current
    if (scroller === null) return
    scrollToConversationEnd(scroller, 'auto')
    revealedQuestionRef.current = undefined
    navigationPausedRef.current = false
    followingEndRef.current = false
    followingRef.current = true
    setNewActivity(false)
  }, [state.threadId])

  useEffect(() => {
    const scroller = scrollerRef.current
    if (scroller === null) return
    const resumeOnReaderInput = (event: Event) => {
      // Focusing transcript content can scroll it (Tab or assistive navigation).
      // The return control focuses the region itself without changing intent.
      if (event.type === 'focusin' && event.target === scroller) return
      if (
        event instanceof KeyboardEvent &&
        !['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)
      )
        return
      navigationPausedRef.current = false
      followingEndRef.current = false
      const pending = pendingHistoryRef.current
      if (pending !== undefined) {
        pending.mode = 'reading'
        pending.anchor = historyReadAnchor(scroller)
      }
    }
    const events = ['wheel', 'touchmove', 'pointerdown', 'keydown', 'focusin'] as const
    for (const event of events) scroller.addEventListener(event, resumeOnReaderInput, { passive: true })
    return () => {
      for (const event of events) scroller.removeEventListener(event, resumeOnReaderInput)
      if (pendingHistoryRef.current?.frame !== undefined) cancelAnimationFrame(pendingHistoryRef.current.frame)
      pendingHistoryRef.current = undefined
    }
  }, [])

  // An unanswered question blocks the chat, so it is revealed even when the reader
  // has scrolled away and ordinary activity would only raise the badge. It targets
  // the question element rather than the transcript end, so a card that finishes
  // laying out after the scroll cannot leave the controls under the composer.
  useEffect(() => {
    if (answerableQuestionKey === undefined || revealedQuestionRef.current === answerableQuestionKey) return
    revealedQuestionRef.current = answerableQuestionKey
    const scroller = scrollerRef.current
    if (scroller === null) return
    const frame = requestAnimationFrame(() => {
      const question = questionRef.current
      if (question !== null && typeof question.scrollIntoView === 'function') {
        question.scrollIntoView({ behavior: conversationScrollBehavior(), block: 'end' })
        if (document.activeElement === document.body) question.focus({ preventScroll: true })
      } else {
        scrollToConversationEnd(scroller, conversationScrollBehavior())
      }
      followingRef.current = true
      followingEndRef.current = false
      navigationPausedRef.current = false
      if (pendingHistoryRef.current !== undefined) pendingHistoryRef.current.anchor = undefined
      setNewActivity(false)
    })
    return () => cancelAnimationFrame(frame)
  }, [answerableQuestionKey])

  useEffect(() => {
    const scroller = scrollerRef.current
    if (scroller === null) return
    const frame = requestAnimationFrame(() => {
      if (followingRef.current) {
        scrollToConversationEnd(scroller, conversationScrollBehavior())
        setNewActivity(false)
      } else {
        setNewActivity(true)
      }
    })
    return () => cancelAnimationFrame(frame)
  }, [projectionRevision])

  const loadEarlier = async () => {
    const scroller = scrollerRef.current
    if (pendingHistoryRef.current !== undefined || scroller === null) return
    const pending: PendingHistoryLoad = {
      historyKey: historyKeyRef.current,
      anchor: historyReadAnchor(scroller),
      mode: 'reading',
      settled: false
    }
    pendingHistoryRef.current = pending
    setHistoryLoad({ threadId: state.threadId, loading: true, error: undefined })
    try {
      await onLoadOlder()
    } catch (loadError) {
      if (pendingHistoryRef.current !== pending) return
      pendingHistoryRef.current = undefined
      setHistoryLoad({
        threadId: state.threadId,
        loading: false,
        error: loadError instanceof Error ? loadError.message : 'Earlier messages could not be loaded.'
      })
      return
    }
    if (pendingHistoryRef.current !== pending) return
    pending.settled = true
    pending.frame = requestAnimationFrame(() => {
      if (pendingHistoryRef.current !== pending) return
      // The controller also resolves handled failures. Only a committed history
      // change warrants compensation, and the anchor tracks the reader's latest
      // scroll/jump rather than the position when the request began.
      if (pending.historyKey !== historyKeyRef.current) {
        if (pending.mode === 'end') scrollToConversationEnd(scroller, 'auto')
        else if (pending.anchor !== undefined && scroller.contains(pending.anchor.element)) {
          const currentTop = scroller.scrollTop
          const delta =
            pending.anchor.element.getBoundingClientRect().top -
            scroller.getBoundingClientRect().top -
            pending.anchor.offset
          // Native scroll anchoring may already have preserved this exact anchor.
          // Finish a pending jump even when native anchoring produced zero delta:
          // its last animation frame may still target the old document geometry.
          if (pending.mode === 'jump' || Math.abs(delta) > 0.5) {
            const top = Math.max(0, currentTop + delta)
            if (typeof scroller.scrollTo === 'function') scroller.scrollTo({ top, behavior: 'instant' })
            else scroller.scrollTop = top
          }
        }
      }
      pendingHistoryRef.current = undefined
      setHistoryLoad({ threadId: state.threadId, loading: false, error: undefined })
    })
  }

  const jumpToTurn = (key: string) => {
    const scroller = scrollerRef.current
    const target = document.getElementById(`${navigationPrefix}-${key}`)
    if (scroller === null || target === null) return
    // A smooth jump starts at the live end. Its first scroll events can still be
    // "near bottom", so only explicit reader input may release this follow pause.
    navigationPausedRef.current = true
    followingEndRef.current = false
    followingRef.current = false
    if (pendingHistoryRef.current !== undefined) {
      pendingHistoryRef.current.mode = 'jump'
      pendingHistoryRef.current.anchor = { element: target, offset: 20 }
    }
    const top = target.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - 20
    scroller.scrollTo({ top: Math.max(0, top), behavior: conversationScrollBehavior() })
  }

  const renderMessage = (
    message: ConversationMessage,
    options: { showArtifact?: boolean; originStatus?: ConversationOriginStatus } = {}
  ) => (
    <ConversationTurn
      key={`message:${message.messageId}`}
      message={message}
      isCurrentUser={message.author === 'user' && message.createdBy === currentUserId}
      showArtifact={options.showArtifact ?? true}
      {...(options.originStatus === undefined ? {} : { originStatus: options.originStatus })}
      {...(outputByMessageID.has(message.messageId) ? { staleness: outputByMessageID.get(message.messageId)! } : {})}
      {...(slideFacts.changeByMessageId.has(message.messageId)
        ? { slideChange: slideFacts.changeByMessageId.get(message.messageId)! }
        : {})}
      onOpenCanvas={onOpenCanvas}
      onOpenInspector={onOpenInspector}
      {...(loadSourceSummaries === undefined ? {} : { loadSourceSummaries })}
    />
  )

  const renderActivityEntry = (entry: TranscriptEntry) => {
    switch (entry.kind) {
      case 'change':
        return (
          <ConversationChangeCard
            key={entry.key}
            change={entry.change}
            onOpenCanvas={onOpenCanvas}
            onOpenInspector={onOpenInspector}
          />
        )
      case 'stopped':
        return (
          <ConversationStoppedCard
            key={entry.key}
            executionId={entry.executionId}
            reason={entry.reason}
            {...(onRetryExecution === undefined ? {} : { onRetry: onRetryExecution })}
          />
        )
      case 'intake-stopped':
        return <ConversationIntakeStoppedCard key={entry.key} reason={entry.reason} />
      case 'exhausted':
        return (
          <ConversationExhaustedCard
            key={entry.key}
            entry={entry.entry}
            {...(onContinueQuestions === undefined ? {} : { onContinue: onContinueQuestions })}
          />
        )
      case 'message':
      case 'attempt':
      case 'question':
        return null
    }
  }

  const renderResults = (resultMessages: Array<ConversationMessage>) => {
    const finalCardMessageByArtifact = new Map<string, number>()
    for (const message of resultMessages) {
      const key = resultArtifactKey(message)
      if (key !== undefined) finalCardMessageByArtifact.set(key, message.messageId)
    }
    const slideMessages = resultMessages.filter(
      (message) => message.presentationSlideId !== undefined && message.presentationSlideId !== null
    )
    const renderResultMessage = (message: ConversationMessage) => {
      const key = resultArtifactKey(message)
      return renderMessage(message, {
        showArtifact: key === undefined || finalCardMessageByArtifact.get(key) === message.messageId
      })
    }
    if (slideMessages.length <= 1) return resultMessages.map(renderResultMessage)
    const slideIds = new Set(slideMessages.map((message) => message.messageId))
    const firstSlideId = slideMessages[0]!.messageId
    return resultMessages.map((message) => {
      if (!slideIds.has(message.messageId)) return renderResultMessage(message)
      if (message.messageId !== firstSlideId) return null
      return (
        <SlideResultDisclosure key={`slides:${firstSlideId}`} count={slideMessages.length}>
          {slideMessages.map(renderResultMessage)}
        </SlideResultDisclosure>
      )
    })
  }

  return (
    <div className="@container/transcript relative flex min-h-0 flex-1 flex-col">
      <ConversationNavigator
        key={state.threadId}
        items={navigationItems}
        activeKey={navigationPosition.key}
        visibleKeys={navigationPosition.visibleKeys}
        targetPrefix={navigationPrefix}
        onJump={jumpToTurn}
        hasOlder={state.hasOlder}
        loadingOlder={loadingOlder}
        olderError={olderError ?? loadOlderError}
        onLoadEarlier={() => void loadEarlier()}
      />
      <div
        ref={scrollerRef}
        data-conversation-scroller
        role="region"
        aria-label="Conversation history"
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- This scrollable region needs a keyboard entry point for native scrolling.
        tabIndex={0}
        className={cn(
          'relative min-h-0 flex-1 [scrollbar-color:var(--muted-foreground)_transparent] overflow-y-auto overscroll-contain pr-[var(--conversation-context-space,0px)] pl-[var(--conversation-leading-gutter,0px)]',
          navigationItems.length >= 2 && '@[32rem]/transcript:[--conversation-leading-gutter:2.5rem]'
        )}
        onScroll={(event) => {
          const pending = pendingHistoryRef.current
          if (pending !== undefined && !pending.settled && pending.mode === 'reading') {
            pending.anchor = historyReadAnchor(event.currentTarget)
          }
          const near = isNearConversationEnd(event.currentTarget)
          followingRef.current = !navigationPausedRef.current && (followingEndRef.current || near)
          if (followingRef.current) setNewActivity(false)
        }}
      >
        <div ref={contentRef} className="mx-auto flex w-full max-w-[44rem] flex-col gap-7 px-4 pt-5 pb-8 sm:px-6">
          {state.hasOlder && (
            <div className="flex flex-col items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void loadEarlier()}
                disabled={loadingOlder}
                aria-label={
                  (olderError ?? loadOlderError) === undefined
                    ? 'Load earlier messages'
                    : 'Try loading earlier messages again'
                }
              >
                {loadingOlder && <LoaderCircle className="size-4 animate-spin motion-reduce:animate-none" />}
                {(olderError ?? loadOlderError) === undefined
                  ? 'Load earlier messages'
                  : 'Try loading earlier messages again'}
              </Button>
              {(olderError ?? loadOlderError) && (
                <p className="text-xs text-destructive" role="alert">
                  {olderError ?? loadOlderError}
                </p>
              )}
            </div>
          )}
          {isEmpty && (
            <div className="flex min-h-[min(42vh,24rem)] flex-col items-center justify-center text-center">
              <h2 className="text-xl font-semibold tracking-tight">
                What do you need from {projectName ?? 'this workspace'}?
              </h2>
              <p className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">
                Describe the work. Snowvault will ask only for details it cannot establish safely.
              </p>
            </div>
          )}
          {turns.map((turn) => {
            const activityRows = turn.activity.filter(isTranscriptActivity).map(renderActivityEntry)
            return (
              <section
                key={turn.key}
                id={`${navigationPrefix}-${turn.key}`}
                data-conversation-turn-key={turn.key}
                data-testid="conversation-turn-group"
                data-turn-phase={turn.phase}
                className="flex flex-col gap-3"
              >
                {turn.originMessage?.author === 'user' &&
                  renderMessage(
                    turn.originMessage,
                    turn.originStatus === undefined ? {} : { originStatus: turn.originStatus }
                  )}
                <ConversationExecutionDisclosure
                  turn={turn}
                  {...(activityRows.length === 0 ? {} : { children: activityRows })}
                  {...(turn.meaningfulActivityCount >= 4 && turn.originMessage !== undefined
                    ? {
                        onViewActivity: () =>
                          onOpenInspector({
                            inspector: 'activity',
                            originMessageId: turn.originMessage!.messageId
                          })
                      }
                    : {})}
                />
                {turn.phase === 'writing' &&
                  turn.attempts.map((entry) => (
                    <article key={entry.key} className="text-[0.95rem] leading-7" aria-label="Snowvault is writing">
                      {entry.attempt.text}
                      <span
                        className="ml-1 inline-block h-4 w-1 rounded-full bg-primary motion-safe:animate-pulse"
                        aria-hidden="true"
                      />
                    </article>
                  ))}
                {turn.clarifications.length > 0 && <ClarificationDisclosure clarifications={turn.clarifications} />}
                {onAnswer !== undefined &&
                  turn.questions.map((question, index) => (
                    <PendingQuestion
                      key={`${question.runId}:${question.suspensionKey}:${question.slotKey}`}
                      question={question}
                      latest={turn.isLatestPendingQuestion && index === turn.questions.length - 1}
                      {...(turn.isLatestPendingQuestion && index === turn.questions.length - 1
                        ? { articleRef: questionRef }
                        : {})}
                      onAnswer={onAnswer}
                      {...(onAbandonQuestion === undefined ? {} : { onAbandon: onAbandonQuestion })}
                    />
                  ))}
                {renderResults(turn.resultMessages)}
              </section>
            )
          })}
          <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
            {liveAnnouncement}
          </p>
        </div>
      </div>
      {(newActivity || navigationPosition.canJumpToEnd) && (
        <Button
          type="button"
          variant={newActivity ? 'default' : 'outline'}
          size={newActivity ? 'sm' : 'icon-lg'}
          aria-label={newActivity ? 'New activity' : 'Jump to latest message'}
          title={newActivity ? 'New activity' : 'Jump to latest message'}
          className={cn(
            'absolute bottom-3 left-[calc((100%+var(--conversation-leading-gutter,0px)-var(--conversation-context-space,0px))/2)] z-10 -translate-x-1/2 shadow-md motion-reduce:transition-none',
            !newActivity && 'rounded-full bg-background dark:bg-background'
          )}
          onClick={() => {
            const scroller = scrollerRef.current
            navigationPausedRef.current = false
            // Keep following through the smooth return and streamed growth. Only
            // a new reader action, history jump, or question reveal releases it.
            followingEndRef.current = true
            followingRef.current = true
            if (pendingHistoryRef.current !== undefined) pendingHistoryRef.current.mode = 'end'
            setNewActivity(false)
            if (scroller !== null) {
              scrollToConversationEnd(scroller, conversationScrollBehavior())
              scroller.focus({ preventScroll: true })
            }
          }}
        >
          <ArrowDown className={newActivity ? 'size-4' : 'size-5'} aria-hidden="true" />
          {newActivity && 'New activity'}
        </Button>
      )}
    </div>
  )
}
